import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ArrowLeft, Loader2, Save, Trash2, Plus, X, ChevronDown, ChevronRight,
    Users, Clock, Target, Package, ClipboardCheck, Anchor, Sliders,
    ShieldCheck, Waves, StickyNote, Map as MapIcon, LogOut, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";
import TrialMap from "@/components/TrialMap";
import TrialPostTest from "@/components/TrialPostTest";

/**
 * /trials/:id — Big trial editor with Pre-Test / Post-Test tabs.
 *
 * Save strategy: field-level PATCH with debounce. Every setField() call
 * updates local state immediately, and after 500ms of quiet, patches to the
 * backend. Loading a trial merges backend response over local state so
 * concurrent operators see each other's changes on refresh (WebSocket
 * realtime is not wired for trials yet — Phase 2+).
 */

const OUTCOMES = ["planned", "in-progress", "success", "partial", "failed", "aborted"];

// Stable per-row key so React can preserve DOM/input focus across delete-mid-list.
// UUID lives on the row in a `_k` field. Persisted through backend (extra="allow").
const makeKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Fields that are arrays of row objects and need `_k` for stable React keys.
const KEYED_ARRAY_FIELDS = [
    "personnel", "timeline", "testing_goals", "gear_list",
    "pre_op_checks", "pre_float_checks", "site_safety_checks",
    "on_water_checks", "parameters", "video_metrics", "test_notes",
];

// Ensure every row in every keyed-array field has a stable `_k`. No-op if already set.
function hydrateKeys(trial) {
    if (!trial) return trial;
    const next = { ...trial };
    for (const field of KEYED_ARRAY_FIELDS) {
        const arr = next[field];
        if (Array.isArray(arr)) {
            next[field] = arr.map((r) =>
                (r && typeof r === "object" && !r._k) ? { ...r, _k: makeKey() } : r
            );
        }
    }
    return next;
}

export default function TrialEditor() {
    const { id } = useParams();
    const nav = useNavigate();
    const [trial, setTrial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("pre");
    const patchQueue = useRef({});
    const patchTimer = useRef(null);

    // ---- Load ----------------------------------------------------------------
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/trials/${id}`);
            setTrial(hydrateKeys(data));
        } catch (e) {
            console.error("[TrialEditor] load failed:", e?.message || e);
            toast.error("Could not load trial.");
            nav("/trials");
        } finally {
            setLoading(false);
        }
    }, [id, nav]);
    useEffect(() => { load(); }, [load]);

    // ---- Debounced PATCH -----------------------------------------------------
    const flush = useCallback(async () => {
        const pending = patchQueue.current;
        if (!Object.keys(pending).length) return;
        patchQueue.current = {};
        setSaving(true);
        try {
            await api.patch(`/trials/${id}`, pending);
        } catch (e) {
            console.error("[TrialEditor] save failed:", e?.message || e);
            toast.error("Save failed — retrying on next edit.");
            // Put failed fields back into the queue so the next change re-tries.
            patchQueue.current = { ...pending, ...patchQueue.current };
        } finally {
            setSaving(false);
        }
    }, [id]);

    const setField = useCallback((path, value) => {
        // path is a top-level field name (e.g. "personnel", "primary_site").
        setTrial((prev) => prev ? { ...prev, [path]: value } : prev);
        patchQueue.current[path] = value;
        if (patchTimer.current) clearTimeout(patchTimer.current);
        patchTimer.current = setTimeout(flush, 500);
    }, [flush]);

    // Flush on unmount so nothing is lost.
    useEffect(() => () => { flush(); }, [flush]);

    // ---- Delete --------------------------------------------------------------
    const deleteTrial = async () => {
        if (!window.confirm(`Delete trial "${trial.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/trials/${id}`);
            toast.success("Trial deleted.");
            nav("/trials");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed.");
        }
    };

    if (loading || !trial) {
        return (
            <div className="min-h-screen bg-virtus-bg flex items-center justify-center">
                <Loader2 className="animate-spin text-virtus-orange" size={24} />
            </div>
        );
    }

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            {/* Editor header — its own header so we can show trial title + save state */}
            <EditorHeader
                trial={trial}
                saving={saving}
                onBack={() => { flush(); nav("/trials"); }}
                onTitleChange={(v) => setField("title", v)}
                onDelete={deleteTrial}
                onExport={() => { flush(); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/trials/${id}/export.pdf`, "_blank"); }}
            />

            {/* Tab switcher */}
            <div className="flex border-b border-virtus-line flex-shrink-0 bg-virtus-bg">
                {[
                    { id: "pre",  label: "PRE-TEST · PLANNING" },
                    { id: "post", label: "POST-TEST · CAPTURE" },
                ].map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`trial-tab-${t.id}`}
                        className={`flex-1 md:flex-none px-4 md:px-8 py-3 font-mono text-[10px] tracking-[0.28em] uppercase transition-colors ${
                            tab === t.id
                                ? "text-virtus-orange border-b-2 border-virtus-orange -mb-px"
                                : "text-virtus-dim hover:text-white"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto" data-kiosk-scroll="true">
                {tab === "pre" ? (
                    <PreTest trial={trial} setField={setField} />
                ) : (
                    <TrialPostTest trial={trial} setField={setField} />
                )}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// EditorHeader — editable title, save state, back + delete
// -----------------------------------------------------------------------------

function EditorHeader({ trial, saving, onBack, onTitleChange, onDelete, onExport }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    return (
        <div className="flex items-center justify-between px-3 md:px-8 py-2 md:py-4 border-b border-virtus-line bg-virtus-bg gap-2 md:gap-6 md:pr-24">
            <div className="flex items-center gap-3 md:gap-5 min-w-0 flex-1">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 md:gap-2 font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange flex-shrink-0"
                    data-testid="trial-back-btn"
                >
                    <ArrowLeft size={14} />
                    <span className="hidden md:inline">TRIALS</span>
                </button>
                <div className="hidden md:block h-6 w-px bg-virtus-line" />
                <div className="min-w-0 flex-1">
                    <div className="font-mono text-[9px] md:text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-orange">
                        AO/06 · TRIAL
                    </div>
                    <input
                        value={trial.title || ""}
                        onChange={(e) => onTitleChange(e.target.value)}
                        placeholder="Untitled Trial"
                        className="w-full bg-transparent font-heading text-lg md:text-2xl font-black tracking-tight uppercase text-white outline-none focus:text-virtus-orange transition-colors"
                        data-testid="trial-title-input"
                    />
                </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                <SaveIndicator saving={saving} />
                <button
                    onClick={onExport}
                    className="flex items-center gap-1.5 md:gap-2 font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-orange hover:text-white border border-virtus-orange px-2 py-2 md:px-3 md:py-2"
                    title="Export PDF report"
                    data-testid="trial-export-pdf-btn"
                >
                    <FileDown size={12} />
                    <span className="hidden md:inline">EXPORT PDF</span>
                </button>
                <button
                    onClick={onDelete}
                    className="p-1.5 md:p-2 border border-virtus-line hover:border-virtus-stuck hover:text-virtus-stuck text-virtus-dim"
                    title="Delete trial"
                    data-testid="trial-delete-btn"
                >
                    <Trash2 size={12} />
                </button>
                <button
                    onClick={() => { logout(); nav("/login"); }}
                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-stuck px-2 py-2 md:px-3 md:py-2 border border-virtus-line hover:border-virtus-stuck"
                    data-testid={TID.logoutBtn}
                    aria-label="Log out"
                >
                    <LogOut size={12} />
                    <span className="hidden md:inline">LOGOUT</span>
                </button>
            </div>
        </div>
    );
}

function SaveIndicator({ saving }) {
    return (
        <div className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.24em] uppercase">
            {saving ? (
                <>
                    <Loader2 size={10} className="animate-spin text-virtus-orange" />
                    <span className="text-virtus-orange">SAVING</span>
                </>
            ) : (
                <>
                    <div className="w-1.5 h-1.5 rounded-full bg-virtus-done" />
                    <span className="text-virtus-dim">SAVED</span>
                </>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// PRE-TEST — all planning sections
// -----------------------------------------------------------------------------

function PreTest({ trial, setField }) {
    return (
        <div className="p-3 md:p-8 space-y-6 max-w-[1400px] mx-auto">
            <HeaderCard trial={trial} setField={setField} />
            <LongTextSection
                title="1 · Mission Overview"
                icon={StickyNote}
                value={trial.mission_overview}
                onChange={(v) => setField("mission_overview", v)}
                placeholder="What are we validating today? What's the primary objective and any known watch items from prior testing?"
                minHeight={140}
                testid="mission-overview-input"
            />
            <PersonnelSection trial={trial} setField={setField} />
            <TimelineSection trial={trial} setField={setField} />
            <ChecklistSection
                title="4 · Testing Goals"
                icon={Target}
                items={trial.testing_goals}
                onChange={(items) => setField("testing_goals", items)}
                placeholder="Add a goal..."
                testidPrefix="testing-goals"
            />
            <LongTextSection
                title="5 · Documentation & Data Control"
                icon={ClipboardCheck}
                value={trial.documentation_notes}
                onChange={(v) => setField("documentation_notes", v)}
                placeholder="Who is documenting? What gets captured? Any anomaly-reporting rules?"
                minHeight={100}
                testid="documentation-notes-input"
            />
            <GearListSection trial={trial} setField={setField} />
            <ChecklistSection
                title="7 · USV Pre-Operational Checks"
                icon={Sliders}
                items={trial.pre_op_checks}
                onChange={(items) => setField("pre_op_checks", items)}
                placeholder="Add a check..."
                testidPrefix="pre-op-checks"
            />
            <NestedChecklistSection
                title="8 · USV Pre-Float Checks"
                icon={Anchor}
                items={trial.pre_float_checks}
                onChange={(items) => setField("pre_float_checks", items)}
                testidPrefix="pre-float-checks"
                extras={<ParametersTable value={trial.parameters} onChange={(v) => setField("parameters", v)} />}
            />
            <ChecklistSection
                title="9 · Site Safety & Logistics"
                icon={ShieldCheck}
                items={trial.site_safety_checks}
                onChange={(items) => setField("site_safety_checks", items)}
                placeholder="Add a safety item..."
                testidPrefix="site-safety-checks"
            />
            <NestedChecklistSection
                title="10 · USV On-Water Checks"
                icon={Waves}
                items={trial.on_water_checks}
                onChange={(items) => setField("on_water_checks", items)}
                testidPrefix="on-water-checks"
            />
            <Section title="11 · Location Map" icon={MapIcon}>
                <TrialMap
                    locations={trial.locations || []}
                    onChange={(next) => setField("locations", next)}
                    primarySite={trial.primary_site}
                    altSite={trial.alternate_site}
                />
                <div className="mt-3 text-[10px] tracking-[0.24em] font-mono uppercase text-virtus-faint">
                    // Pick a tool · click the map · rename in the side panel · drag pins to fine-tune
                </div>
            </Section>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Section: Header card (Date / Start / Sites / Status / Vessel / Lead)
// -----------------------------------------------------------------------------

function HeaderCard({ trial, setField }) {
    return (
        <section className="border border-virtus-line bg-virtus-surface">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-virtus-line">
                <HeaderField label="Trial date"       value={trial.trial_date}       onChange={(v) => setField("trial_date", v)} type="date" testid="trial-date-input" />
                <HeaderField label="Start window"     value={trial.start_window}     onChange={(v) => setField("start_window", v)} placeholder="0600 / 0700"    testid="trial-start-input" />
                <HeaderField label="Status"           value={trial.status}           onChange={(v) => setField("status", v)}       placeholder="Active — Pending Weather" testid="trial-status-input" />
                <HeaderField label="Primary site"     value={trial.primary_site}     onChange={(v) => setField("primary_site", v)}   placeholder="Cahooque Boat Launch Dock" testid="trial-primary-site-input" />
                <HeaderField label="Alternate site"   value={trial.alternate_site}   onChange={(v) => setField("alternate_site", v)} placeholder="Neuse River" testid="trial-alt-site-input" />
                <HeaderField label="Vessel callsign"  value={trial.vessel_callsign}  onChange={(v) => setField("vessel_callsign", v)} placeholder="VM18-2" testid="trial-vessel-input" />
                <HeaderField label="Test lead"        value={trial.lead}             onChange={(v) => setField("lead", v)}          placeholder="operator@virtusmaritime.com" testid="trial-lead-input" />
                <div className="p-3 md:p-4 bg-virtus-surface">
                    <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">Outcome</label>
                    <select
                        value={trial.outcome}
                        onChange={(e) => setField("outcome", e.target.value)}
                        className="w-full bg-transparent border-0 border-b border-virtus-line focus:border-virtus-orange px-0 py-1.5 text-white outline-none font-mono text-xs uppercase tracking-widest"
                        data-testid="trial-outcome-select"
                    >
                        {OUTCOMES.map((o) => (
                            <option key={o} value={o}>{o.toUpperCase()}</option>
                        ))}
                    </select>
                </div>
            </div>
        </section>
    );
}

function HeaderField({ label, value, onChange, placeholder, type = "text", testid }) {
    return (
        <div className="p-3 md:p-4 bg-virtus-surface">
            <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">
                {label}
            </label>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-transparent border-0 border-b border-virtus-line focus:border-virtus-orange px-0 py-1.5 text-white outline-none font-mono text-xs"
            />
        </div>
    );
}

// -----------------------------------------------------------------------------
// Reusable section shell
// -----------------------------------------------------------------------------

function Section({ title, icon: Icon, children }) {
    return (
        <section className="border border-virtus-line bg-virtus-surface">
            <header className="flex items-center gap-3 px-4 md:px-5 py-3 border-b border-virtus-line">
                {Icon && <Icon size={14} className="text-virtus-orange" />}
                <h2 className="font-heading text-sm md:text-base font-bold tracking-tight uppercase text-white">
                    {title}
                </h2>
            </header>
            <div className="p-4 md:p-5">
                {children}
            </div>
        </section>
    );
}

function LongTextSection({ title, icon, value, onChange, placeholder, minHeight = 100, testid }) {
    return (
        <Section title={title} icon={icon}>
            <textarea
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange rounded-none p-3 text-white outline-none font-sans text-sm leading-relaxed resize-y"
                style={{ minHeight }}
            />
        </Section>
    );
}

// -----------------------------------------------------------------------------
// Section: Personnel & Vehicle Assignments
// -----------------------------------------------------------------------------

function PersonnelSection({ trial, setField }) {
    const rows = trial.personnel || [];
    const set = (idx, k, v) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, [k]: v } : r));
        setField("personnel", next);
    };
    const add = () => setField("personnel", [...rows, { _k: makeKey(), name: "", role: "", vehicle: "" }]);
    const remove = (i) => setField("personnel", rows.filter((_, x) => x !== i));

    return (
        <Section title="2 · Personnel & Vehicle Assignments" icon={Users}>
            {rows.length > 0 && (
                <div className="hidden md:grid grid-cols-[1fr_1.5fr_1.2fr_28px] gap-2 mb-2 font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-dim">
                    <span>Personnel</span>
                    <span>Assignment</span>
                    <span>Vehicle / Tow</span>
                    <span></span>
                </div>
            )}
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={r._k || `p-${i}`} className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_1.2fr_28px] gap-2 items-center">
                        <input value={r.name || ""} onChange={(e) => set(i, "name", e.target.value)}
                            placeholder="Name"
                            className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                            data-testid={`personnel-name-${i}`} />
                        <input value={r.role || ""} onChange={(e) => set(i, "role", e.target.value)}
                            placeholder="Role / Assignment"
                            className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                            data-testid={`personnel-role-${i}`} />
                        <input value={r.vehicle || ""} onChange={(e) => set(i, "vehicle", e.target.value)}
                            placeholder="Vehicle / Tow"
                            className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                            data-testid={`personnel-vehicle-${i}`} />
                        <button onClick={() => remove(i)} className="text-virtus-dim hover:text-virtus-stuck justify-self-end md:justify-self-center" title="Remove">
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={add} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="personnel-add-btn">
                <Plus size={11} /> Add row
            </button>
        </Section>
    );
}

// -----------------------------------------------------------------------------
// Section: Timeline
// -----------------------------------------------------------------------------

function TimelineSection({ trial, setField }) {
    const rows = trial.timeline || [];
    const setEvt = (idx, k, v) => setField("timeline", rows.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
    const setBullet = (evtIdx, bIdx, v) => {
        const bullets = [...(rows[evtIdx].bullets || [])];
        bullets[bIdx] = v;
        setEvt(evtIdx, "bullets", bullets);
    };
    const addEvt = () => setField("timeline", [...rows, { _k: makeKey(), time: "", header: "", bullets: [] }]);
    const removeEvt = (i) => setField("timeline", rows.filter((_, x) => x !== i));
    const addBullet = (i) => setEvt(i, "bullets", [...(rows[i].bullets || []), ""]);
    const removeBullet = (evtIdx, bIdx) => setEvt(evtIdx, "bullets", (rows[evtIdx].bullets || []).filter((_, x) => x !== bIdx));

    return (
        <Section title="3 · Timeline" icon={Clock}>
            <div className="space-y-4">
                {rows.map((r, i) => (
                    <div key={r._k || `t-${i}`} className="grid grid-cols-[80px_1fr_28px] gap-2 items-start">
                        <input value={r.time || ""} onChange={(e) => setEvt(i, "time", e.target.value)}
                            placeholder="0600"
                            className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-virtus-orange outline-none font-mono text-xs" />
                        <div className="space-y-2">
                            <input value={r.header || ""} onChange={(e) => setEvt(i, "header", e.target.value)}
                                placeholder="Event header (e.g., 'Launch & Staging')"
                                className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs font-bold" />
                            {(r.bullets || []).map((b, bi) => (
                                <div key={`${r._k || i}-b-${bi}`} className="flex items-center gap-2 pl-4">
                                    <span className="text-virtus-dim">·</span>
                                    <input value={b} onChange={(e) => setBullet(i, bi, e.target.value)}
                                        placeholder="Bullet…"
                                        className="flex-1 bg-transparent border-0 border-b border-virtus-line focus:border-virtus-orange px-0 py-1 text-white outline-none text-sm" />
                                    <button onClick={() => removeBullet(i, bi)} className="text-virtus-dim hover:text-virtus-stuck">
                                        <X size={11} />
                                    </button>
                                </div>
                            ))}
                            <button onClick={() => addBullet(i)} className="text-[10px] tracking-[0.24em] uppercase font-mono text-virtus-dim hover:text-virtus-orange pl-4">
                                + bullet
                            </button>
                        </div>
                        <button onClick={() => removeEvt(i)} className="text-virtus-dim hover:text-virtus-stuck mt-2">
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={addEvt} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="timeline-add-btn">
                <Plus size={11} /> Add event
            </button>
        </Section>
    );
}

// -----------------------------------------------------------------------------
// Section: generic Checklist (flat)
// -----------------------------------------------------------------------------

function ChecklistSection({ title, icon, items, onChange, placeholder, testidPrefix }) {
    const rows = items || [];
    const toggle = (i) => onChange(rows.map((r, x) => (x === i ? { ...r, done: !r.done } : r)));
    const setText = (i, v) => onChange(rows.map((r, x) => (x === i ? { ...r, text: v } : r)));
    const add = () => onChange([...rows, { _k: makeKey(), text: "", done: false }]);
    const remove = (i) => onChange(rows.filter((_, x) => x !== i));
    return (
        <Section title={title} icon={icon}>
            <div className="space-y-1">
                {rows.map((r, i) => (
                    <ChecklistRow
                        key={r._k || `c-${i}`}
                        checked={r.done}
                        text={r.text}
                        onToggle={() => toggle(i)}
                        onText={(v) => setText(i, v)}
                        onRemove={() => remove(i)}
                        testid={`${testidPrefix}-item-${i}`}
                    />
                ))}
            </div>
            <button onClick={add} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid={`${testidPrefix}-add-btn`}>
                <Plus size={11} /> {placeholder}
            </button>
        </Section>
    );
}

function ChecklistRow({ checked, text, onToggle, onText, onRemove, testid, nested = false }) {
    return (
        <div className={`group flex items-center gap-2 py-1 ${nested ? "pl-8" : ""}`}>
            <button
                onClick={onToggle}
                className={`flex-shrink-0 w-4 h-4 border ${checked ? "bg-virtus-orange border-virtus-orange" : "border-virtus-line"} transition-colors`}
                data-testid={`${testid}-check`}
                aria-label={checked ? "Uncheck" : "Check"}
            >
                {checked && (
                    <svg viewBox="0 0 16 16" className="w-3 h-3 mx-auto text-black" fill="currentColor">
                        <path d="M6 11.5L2.5 8 4 6.5 6 8.5 12 2.5 13.5 4z" />
                    </svg>
                )}
            </button>
            <input
                value={text || ""}
                onChange={(e) => onText(e.target.value)}
                className={`flex-1 bg-transparent border-0 border-b border-transparent focus:border-virtus-orange px-0 py-1 outline-none text-sm ${checked ? "text-virtus-dim line-through" : "text-white"}`}
                data-testid={testid}
                placeholder="…"
            />
            <button onClick={onRemove} className="text-virtus-dim hover:text-virtus-stuck opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={11} />
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Section: Nested Checklist (item + sub-bullets)
// -----------------------------------------------------------------------------

function NestedChecklistSection({ title, icon, items, onChange, testidPrefix, extras }) {
    const rows = items || [];
    const toggle = (i) => onChange(rows.map((r, x) => (x === i ? { ...r, done: !r.done } : r)));
    const setText = (i, v) => onChange(rows.map((r, x) => (x === i ? { ...r, text: v } : r)));
    const add = () => onChange([...rows, { _k: makeKey(), text: "", done: false, sub: [] }]);
    const remove = (i) => onChange(rows.filter((_, x) => x !== i));
    const toggleSub = (i, si) => onChange(rows.map((r, x) => (x === i ? {
        ...r,
        sub: (r.sub || []).map((s, sx) => sx === si ? { ...s, done: !s.done } : s),
    } : r)));
    const setSubText = (i, si, v) => onChange(rows.map((r, x) => (x === i ? {
        ...r,
        sub: (r.sub || []).map((s, sx) => sx === si ? { ...s, text: v } : s),
    } : r)));
    const addSub = (i) => onChange(rows.map((r, x) => (x === i ? { ...r, sub: [...(r.sub || []), { _k: makeKey(), text: "", done: false }] } : r)));
    const removeSub = (i, si) => onChange(rows.map((r, x) => (x === i ? { ...r, sub: (r.sub || []).filter((_, sx) => sx !== si) } : r)));

    return (
        <Section title={title} icon={icon}>
            <div className="space-y-1">
                {rows.map((r, i) => (
                    <div key={r._k || `n-${i}`}>
                        <ChecklistRow
                            checked={r.done}
                            text={r.text}
                            onToggle={() => toggle(i)}
                            onText={(v) => setText(i, v)}
                            onRemove={() => remove(i)}
                            testid={`${testidPrefix}-item-${i}`}
                        />
                        {(r.sub || []).map((s, si) => (
                            <ChecklistRow
                                key={s._k || `${r._k || i}-s-${si}`}
                                checked={s.done}
                                text={s.text}
                                onToggle={() => toggleSub(i, si)}
                                onText={(v) => setSubText(i, si, v)}
                                onRemove={() => removeSub(i, si)}
                                testid={`${testidPrefix}-item-${i}-sub-${si}`}
                                nested
                            />
                        ))}
                        <button onClick={() => addSub(i)} className="pl-8 py-1 text-[10px] tracking-[0.24em] uppercase font-mono text-virtus-dim hover:text-virtus-orange" data-testid={`${testidPrefix}-item-${i}-sub-add`}>
                            + sub-bullet
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={add} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid={`${testidPrefix}-add-btn`}>
                <Plus size={11} /> Add check
            </button>
            {extras}
        </Section>
    );
}

// -----------------------------------------------------------------------------
// Section: Gear List (flat checklist + optional "Additional Supplies" tag)
// -----------------------------------------------------------------------------

function GearListSection({ trial, setField }) {
    const items = trial.gear_list || [];
    const main = items.filter((g) => !g.is_additional);
    const additional = items.filter((g) => g.is_additional);
    const rewrite = (next) => setField("gear_list", next);
    const toggle = (item) => rewrite(items.map((g) => g === item ? { ...g, done: !g.done } : g));
    const setText = (item, v) => rewrite(items.map((g) => g === item ? { ...g, text: v } : g));
    const remove = (item) => rewrite(items.filter((g) => g !== item));
    const addMain = () => rewrite([...items, { _k: makeKey(), text: "", done: false, is_additional: false }]);
    const addExtra = () => rewrite([...items, { _k: makeKey(), text: "", done: false, is_additional: true }]);
    return (
        <Section title="6 · Gear List" icon={Package}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                {main.map((g, i) => (
                    <ChecklistRow
                        key={g._k || `m-${i}`}
                        checked={g.done}
                        text={g.text}
                        onToggle={() => toggle(g)}
                        onText={(v) => setText(g, v)}
                        onRemove={() => remove(g)}
                        testid={`gear-item-${i}`}
                    />
                ))}
            </div>
            <button onClick={addMain} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="gear-add-btn">
                <Plus size={11} /> Add gear
            </button>

            <div className="mt-6 pt-4 border-t border-virtus-line">
                <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-virtus-dim mb-3">
                    // ADDITIONAL SUPPLIES
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                    {additional.map((g, i) => (
                        <ChecklistRow
                            key={g._k || `a-${i}`}
                            checked={g.done}
                            text={g.text}
                            onToggle={() => toggle(g)}
                            onText={(v) => setText(g, v)}
                            onRemove={() => remove(g)}
                            testid={`gear-extra-item-${i}`}
                        />
                    ))}
                </div>
                <button onClick={addExtra} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="gear-extra-add-btn">
                    <Plus size={11} /> Add supply
                </button>
            </div>
        </Section>
    );
}

// -----------------------------------------------------------------------------
// Section: Mission-Planner Parameters (name/value pairs)
// -----------------------------------------------------------------------------

function ParametersTable({ value, onChange }) {
    const rows = value || [];
    const set = (i, k, v) => onChange(rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
    const add = () => onChange([...rows, { _k: makeKey(), name: "", value: "" }]);
    const remove = (i) => onChange(rows.filter((_, x) => x !== i));
    return (
        <div className="mt-5 pt-4 border-t border-virtus-line">
            <div className="flex items-center gap-2 mb-3">
                <Sliders size={12} className="text-virtus-orange" />
                <h3 className="font-heading text-xs md:text-sm font-bold tracking-tight uppercase text-white">
                    Mission Planner Parameters
                </h3>
                <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-dim">
                    · pre-float ArduPilot values
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {rows.map((r, i) => (
                    <div key={r._k || `param-${i}`} className="group flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange block mb-0.5 truncate" htmlFor={`param-value-${i}`}>
                                <input
                                    value={r.name || ""}
                                    onChange={(e) => set(i, "name", e.target.value)}
                                    placeholder="PARAM_NAME"
                                    aria-label="Parameter name"
                                    className="bg-transparent w-full outline-none font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange"
                                    data-testid={`parameter-name-${i}`}
                                />
                            </label>
                            <input
                                id={`param-value-${i}`}
                                value={r.value || ""}
                                onChange={(e) => set(i, "value", e.target.value)}
                                placeholder="value"
                                aria-label="Parameter value"
                                className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                                data-testid={`parameter-value-${i}`}
                            />
                        </div>
                        <button onClick={() => remove(i)} className="text-virtus-dim hover:text-virtus-stuck opacity-0 group-hover:opacity-100 transition-opacity mt-4" aria-label="Remove parameter">
                            <X size={12} />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={add} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="parameter-add-btn">
                <Plus size={11} /> Add parameter
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// POST-TEST placeholder
// -----------------------------------------------------------------------------

function PostTest({ trial, setField }) {
    return (
        <div className="p-3 md:p-8 max-w-[1400px] mx-auto space-y-6">
            <Section title="Post-Test Capture" icon={ClipboardCheck}>
                <div className="text-center py-12 text-virtus-dim text-sm">
                    <ClipboardCheck size={40} className="text-virtus-orange mx-auto mb-4" strokeWidth={1} />
                    <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-virtus-orange mb-2">
                        // PHASE 3
                    </div>
                    <p className="max-w-md mx-auto leading-relaxed">
                        Weather · Engine metrics · Autonomy metrics · Video metrics · Photo/video
                        upload · Testing notes table · PDF export — shipping next.
                    </p>
                </div>
            </Section>

            {/* Quick post-test that already exists on legacy trials */}
            <Section title="Legacy Fields" icon={StickyNote}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <LabeledInput label="Weather"     value={trial.weather}         onChange={(v) => setField("weather", v)} />
                    <LabeledInput label="Sea state"   value={trial.sea_state}       onChange={(v) => setField("sea_state", v)} />
                    <LabeledTextarea label="Objectives (legacy)"  value={trial.objectives}      onChange={(v) => setField("objectives", v)} />
                    <LabeledTextarea label="Lessons learned"      value={trial.lessons_learned} onChange={(v) => setField("lessons_learned", v)} />
                    <LabeledTextarea label="Free-form notes"      value={trial.notes}           onChange={(v) => setField("notes", v)} full />
                </div>
            </Section>
        </div>
    );
}

function LabeledInput({ label, value, onChange, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">{label}</label>
            <input value={value || ""} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs" />
        </div>
    );
}
function LabeledTextarea({ label, value, onChange, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">{label}</label>
            <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm resize-y min-h-[80px]" />
        </div>
    );
}
